import {
  SERVICE_MESSAGE_MAX,
  type ServiceAuthor,
  type ServiceMessage,
  type ServiceTicketDetail,
} from '@avrahami/contracts';
import { useState, type SyntheticEvent } from 'react';
import { useAsyncAction } from './auth';
import { Alert, Button, TextAreaField } from './components';
import { formatDateTime } from './format';

/** The texts of a ticket's conversation; they come from the caller's dictionary. */
export interface ServiceThreadWords {
  /** The writer's name when the platform's staff wrote. */
  platform: string;
  /** The writer's name when a member of the store wrote and it is not known. */
  store: string;
  empty: string;
  reply: string;
  send: string;
  sent: string;
}

/**
 * The messages of one service ticket and a box to answer (adr/0036). The store's admin and the
 * platform admin show the same thread, each over its own routes; the caller's own side is on the
 * start edge, the other side's messages on the end edge.
 */
export function ServiceThread({
  detail,
  viewer,
  words,
  canReply,
  onReply,
  errorMessage,
  timeZone,
}: {
  detail: ServiceTicketDetail;
  viewer: ServiceAuthor;
  words: ServiceThreadWords;
  canReply: boolean;
  /** Sends the message; the caller refreshes what it shows from the answer. */
  onReply: (body: string) => Promise<unknown>;
  errorMessage: (error: unknown) => string;
  timeZone?: string;
}) {
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const send = useAsyncAction(onReply);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (body.trim() === '') return;
    setSent(false);
    const result = await send.run(body.trim());
    if (result.ok) {
      setBody('');
      setSent(true);
    }
  };
  const name = (message: ServiceMessage) =>
    message.author === 'PLATFORM' ? words.platform : (message.authorName ?? words.store);
  return (
    <div className="flex flex-col gap-4" data-testid="service-thread">
      {detail.messages.length === 0 ? (
        <p className="text-sm text-slate-500">{words.empty}</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {detail.messages.map((message) => {
            const own = message.author === viewer;
            return (
              <li
                key={message.id}
                data-testid="service-message"
                data-author={message.author}
                className={`max-w-[85%] rounded-lg px-4 py-3 text-sm ${
                  own
                    ? 'self-start bg-brand-50 ring-1 ring-brand-100'
                    : 'self-end bg-slate-50 ring-1 ring-slate-200'
                }`}
              >
                <div className="mb-1 flex flex-wrap items-baseline gap-x-3 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">{name(message)}</span>
                  <time dateTime={message.createdAt}>
                    {formatDateTime(message.createdAt, timeZone)}
                  </time>
                </div>
                <p className="whitespace-pre-wrap break-words text-slate-800">{message.body}</p>
              </li>
            );
          })}
        </ol>
      )}
      {canReply ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <TextAreaField
            label={words.reply}
            name="service-reply"
            rows={3}
            maxLength={SERVICE_MESSAGE_MAX}
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              setSent(false);
            }}
          />
          {send.error === null ? null : <Alert>{errorMessage(send.error)}</Alert>}
          <div className="flex items-center gap-3">
            <Button type="submit" busy={send.busy} disabled={body.trim() === ''}>
              {words.send}
            </Button>
            {sent ? (
              <span role="status" className="text-xs text-green-800">
                {words.sent}
              </span>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}
